import { addMinutes, isValid } from 'date-fns';
import { SexType } from '../types';
import {
  evaluateExpression,
  injectToStringMethodToDictionaryValues,
} from './expression-eval';
import { localFormat, localFormatDuration } from './date-helper';
import { localize } from './locale';

interface ExpressionFunction {
  name: string;
  signature: string; // the whole signature with parameters
  func: (...args: any[]) => any;
}

export function getBaseExecutionContext(): Record<string, any> {
  const expressionEvaluationContext: Record<string, any> = {};
  for (const expressionFunction of expressionFunctions) {
    expressionEvaluationContext[expressionFunction.name] =
      expressionFunction.func;
  }
  return expressionEvaluationContext;
}
export const expressionFunctions: ExpressionFunction[] = [
  {
    name: 'iif',
    signature: 'iif(value: boolean, trueValue: string, falseValue: string)',
    func: iif,
  },
  {
    name: 'not',
    signature: 'not(value: boolean)',
    func: not,
  },
  {
    // if `value` is one of the `sourceValues`, returns `resultValues` at the same index.
    // if `value` is not found among `sourceValues`, returns the `value` as is.
    name: 'convert',
    signature:
      'convert(value: SexType, sourceValues: string[], resultValues: string[])',
    func: convert,
  },
  {
    name: 'sexToString',
    signature:
      'sexToString(value: SexType, maleValue: string, femaleValue: string, undefinedValue: string)',
    func: sexToString,
  },
  {
    name: 'evaluationTypeToString',
    signature: 'evaluationTypeToString(value: number))',
    func: evaluationTypeToString,
  },
  {
    name: 'replaceValues',
    signature:
      'replaceValues(value: string, oldSubstring: string, newSubstring: string)',
    func: replaceValues,
  },
  {
    name: 'coalesce',
    signature: 'coalesce(firstValue: any, secondValue: any)',
    func: coalesce,
  },
  {
    name: 'prefixIfNotNull',
    signature: 'prefixIfNotNull(value: string, prefix: string)',
    func: prefixIfNotNull,
  },
  {
    name: 'suffixIfNotNull',
    signature: 'suffixIfNotNull(value: string, suffix: string)',
    func: suffixIfNotNull,
  },
  {
    name: 'substr',
    signature: 'substr(value: string, index: number, length: number)',
    func: substr,
  },
  {
    name: 'indexOf',
    signature: 'indexOf(value: string, substr: string)',
    func: indexOf,
  },
  {
    name: 'formatDate',
    signature: 'formatDate(value: Date, format: string)',
    func: formatDate,
  },
  {
    name: 'formatNumber',
    signature: 'formatNumber(value: number, digits: number)',
    func: formatNumber,
  },
  {
    name: 'currentDate',
    signature: 'currentDate(format?: string)',
    func: currentDate,
  },
  {
    name: 'currentTime',
    signature: 'currentTime(format?: string)',
    func: currentTime,
  },
  {
    name: 'currentTimeInMilliseconds',
    signature: 'currentTimeInMilliseconds()',
    func: currentTimeInMilliseconds,
  },
  {
    name: 'verticalTable',
    signature:
      'verticalTable(items, row1Value: expression, row2Value: expression, ...)',
    func: verticalTable,
  },
  {
    name: 'verticalTableWithHeader',
    signature:
      'verticalTableWithHeader(items, row1Header: string, row1Value: expression, row2Header: string, row2Value: expression, ...)',
    func: verticalTableWithHeader,
  },
  {
    name: 'horizontalTable',
    signature:
      'horizontalTable(items, col1Value: expression, col2Value: expression, ...)',
    func: horizontalTable,
  },
  {
    name: 'horizontalTableWithHeader',
    signature:
      'horizontalTableWithHeader(items, col1Header: string, col1Value: expression, col2Header: string, col2Value: expression, ...)',
    func: horizontalTableWithHeader,
  },
  {
    name: 'dictionaryExpression',
    signature:
      'dictionaryExpression(value: Dictionary, dictionaryExpression: string)',
    func: dictionaryExpression,
  },
  {
    name: 'map',
    signature: 'map(items, expression)',
    func: map,
  },
  {
    name: 'reverse',
    signature: 'reverse(items)',
    func: reverse,
  },
  {
    name: 'trim',
    signature: 'trim(value: string)',
    func: trim,
  },
  {
    name: 'ucase',
    signature: 'ucase(value: string)',
    func: ucase,
  },
  {
    name: 'date',
    signature: 'date(value: string)',
    func: date,
  },
  {
    name: 'formatDuration',
    // allowed formats: y, M, d, h, m, s
    signature: 'formatDuration(value: string, format: string)',
    func: formatDocumentDuration,
  },
  {
    name: 'newLine',
    signature: 'newLine()',
    func: newLine,
  },
  {
    name: 'join',
    signature: 'join(items, separator)',
    func: join,
  },
  {
    name: 'localize',
    signature: 'localize(string)',
    func: localize,
  },

  // when adding new functions here, don't forget to add them to wiki page:
  // https://dev.azure.com/prismacode/Unicorn/_wiki/wikis/Unicorn.wiki/1453/Documents
];

function iif(value: boolean, trueValue: string, falseValue: string): string {
  return convertToBool(value) ? trueValue : falseValue;
}

function not(value: boolean): any {
  return !value;
}

function convertToBool(value: any): boolean {
  return !!value;
}

function convert(value: any, sourceValues: any[], resultValues: any[]): string {
  for (let i = 0; i < sourceValues.length; i++) {
    const sourceValue = sourceValues[i];
    if (sourceValue === false) {
      if (!value) {
        return resultValues[i];
      }
    } else if (sourceValue === true) {
      if (value) {
        return resultValues[i];
      }
    } else if (sourceValue == value) {
      return resultValues[i];
    }
  }
  return value;
}

function sexToString(
  value: SexType,
  maleValue: string,
  femaleValue: string,
  undefinedValue?: string
): string | null {
  switch (value) {
    case SexType.Male:
      return maleValue;
    case SexType.Female:
      return femaleValue;
    case SexType.Undefined:
      return undefinedValue ?? null;
    default:
      return null;
  }
}

function evaluationTypeToString(value: number) {
  switch (value) {
    case 0:
      return 'TIB (less artifact times)';
    case 1:
      return 'TST (less artifact times)';
    case 2:
      return 'TIB';
    case 3:
      return 'SPT';
    case 4:
      return 'TST';

    default:
      return null;
  }
}

function prefixIfNotNull(value: string, prefix: string): string {
  if (value) {
    return prefix ? `${prefix}${value}` : value;
  }
  return '';
}

function suffixIfNotNull(value: string, suffix: string): string {
  if (value) {
    return suffix ? `${value}${suffix}` : value;
  }
  return '';
}

function formatNumber(value: number, digits: number): string | undefined {
  if (isNaN(value)) {
    return undefined;
  }
  return value.toFixed(digits);
}

function formatDate(
  value: string | Date,
  formatString: string
): string | undefined {
  if (value === null || value === undefined) return undefined;

  const date = getDateFromUnknown(value);
  if (!isValid(date)) return undefined;
  return localFormat(date, formatString);
}

function getDateFromUnknown(value: string | Date): Date {
  if (value instanceof Date) return value;
  if (isNaN(value as any)) {
    return new Date(value);
  } else {
    const date = new Date(parseInt(value));
    return addMinutes(date, date.getTimezoneOffset());
  }
}

function substr(value: string, index: number, length: number): string {
  return value.toString().substr(index, length);
}

function indexOf(value: string, substr: string): number {
  return value.toString().indexOf(substr);
}

function coalesce(...args: any[]): any {
  if (args.length === 0) {
    return null;
  }
  for (const arg of args) {
    if (arg) {
      return arg;
    }
  }
  return null;
}

function replaceValues(
  value: string,
  oldSubstring: string,
  newSubstring: string
): string {
  let resultValue = value;
  // replaceAll is not supported in all browsers (e.g. Chrome >= 85 only), so we need to use something else
  resultValue = resultValue.split(oldSubstring).join(newSubstring);
  return resultValue;
}

function currentDate(formatString?: string): string {
  formatString = formatString ?? 'P';
  return localFormat(new Date(), formatString);
}

function currentTime(formatString?: string): string {
  formatString = formatString ?? 'p';
  return localFormat(new Date(), formatString);
}

function currentTimeInMilliseconds(): number {
  const time = new Date();
  return time.getHours() * 3600000 + time.getMinutes() * 60000;
}

function horizontalTable(items: any[], ...expressions: any[]) {
  // actual rendering of tables happens in 'table-helper'.createTableWithCells
  const cells: (string | null)[][] = items.map(() =>
    expressions.map(() => null)
  );

  expressions.forEach((expression, columnIndex) => {
    const calculatedRow = map(items, expression);
    items.forEach((_, index) => {
      cells[index][columnIndex] = calculatedRow[index];
    });
  });

  return {
    type: 'table',
    cells: cells,
  } as TableValue;
}

function verticalTableWithHeader(items: any[], ...expressionWithHeader: any[]) {
  const expressions = expressionWithHeader.filter(
    (_, index) => index % 2 === 1
  );
  const rowHeaders = expressionWithHeader.filter((_, index) => index % 2 === 0);
  const table = verticalTable(items, ...expressions);

  table.cells.forEach((row, index) => {
    row.unshift(rowHeaders[index]);
  });
  return table;
}
function horizontalTableWithHeader(
  items: any[],
  ...expressionWithHeader: any[]
) {
  const expressions = expressionWithHeader.filter(
    (_, index) => index % 2 === 1
  );
  const colHeaders = expressionWithHeader.filter((_, index) => index % 2 === 0);
  const table = horizontalTable(items, ...expressions);
  table.cells.unshift(colHeaders);
  return table;
}
function verticalTable(items: any[], ...expressions: any[]) {
  // actual rendering of tables happens in 'table-helper'.createTableWithCells
  const cells: (string | null)[][] = expressions.map(() =>
    items.map(() => null)
  );

  expressions.forEach((expression, rowIndex) => {
    const calculatedRow = map(items, expression);
    items.forEach((_, colIndex) => {
      cells[rowIndex][colIndex] = calculatedRow[colIndex];
    });
  });

  return {
    type: 'table',
    cells: cells,
  } as TableValue;
}

/*
Calculates `dictionaryExpression` for dictionary element passed as `dictionaryValue`
`dictionaryValue` is expected to be like:
  {"Id":73,"Value":"Insur 123","IsDeleted":false, "Zxczxc":"qwerqwer","Address":"asdfasdf"}
  expression could be like:
  `
 */
function dictionaryExpression(
  dictionaryValue: any,
  dictionaryExpression: string
) {
  try {
    if (Array.isArray(dictionaryValue)) {
      const result = dictionaryValue
        .map((x) =>
          evaluateExpression(dictionaryExpression, localizeDictionaryColumns(x))
        )
        .join(', ');
      return result;
    } else {
      const result = evaluateExpression(
        dictionaryExpression,
        localizeDictionaryColumns(dictionaryValue)
      );
      return result;
    }
  } catch (e) {
    return dictionaryValue;
  }
}

function localizeDictionaryColumns(dictionaryValue: any) {
  Object.keys(dictionaryValue).forEach((key) => {
    const value = dictionaryValue[key];
    if (value && typeof value === 'string') {
      if (value.includes('dictionaries.')) {
        dictionaryValue[key] = localize(value);
      }
    }
    if (key.includes(' ')) {
      dictionaryValue[key.replace(' ', '_')] = dictionaryValue[key];
    }
  });
  return dictionaryValue;
}

function trim(value: string) {
  return (value?.toString() ?? '').trim();
}
function ucase(value: string) {
  return (value?.toString() ?? '').toUpperCase();
}
function date(value: string) {
  return new Date(value);
}
export function newLine() {
  return '\r\n';
}
export function join(items: any[], separator: string) {
  if (Array.isArray(items)) {
    return items.join(separator);
  } else {
    return items;
  }
}
function getArrayFromDataBlockField(items: any) {
  if (Array.isArray(items)) {
    return items;
  } else if (items.type === 'table' && Array.isArray(items.rows)) {
    return items.rows;
  } else {
    return items;
  }
}
export function reverse(items: any) {
  try {
    items = getArrayFromDataBlockField(items);
    if (Array.isArray(items)) {
      return items.reverse();
    } else {
      return items;
    }
  } catch (e) {
    return items;
  }
}
export function map(items: any, expression: string) {
  try {
    const expressionEvaluationContext: Record<string, any> =
      getBaseExecutionContext();

    items = getArrayFromDataBlockField(items);
    if (Array.isArray(items)) {
      const result = items.map((x: any) => {
        expressionEvaluationContext.row = x;
        try {
          const rowResult = evaluateExpression(
            expression,
            expressionEvaluationContext
          );
          return rowResult;
        } catch (e) {
          return undefined;
        }
      });
      return injectToStringMethodToDictionaryValues(result);
    } else {
      try {
        expressionEvaluationContext.row = items;
        const result = evaluateExpression(
          expression,
          expressionEvaluationContext
        );
        return result;
      } catch (e) {
        return undefined;
      }
    }
  } catch (e) {
    return undefined;
  }
}
function formatDocumentDuration(value: Date, formatString?: string) {
  return localFormatDuration(value as unknown as number, formatString ?? 'm');
}

export type TableValue = {
  type: 'table';
  cells: string[][];
};
